import React, { useCallback, useEffect, useState } from "react";
import { Modal, Tag, Typography, Space, Button, message, Progress } from "antd";
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useTranslate, useApiUrl } from "@pankod/refine-core";
import axios from "axios";
import { IQuiz, IQuizAICreation, IQuizAICreationItem } from "interfaces";
import ReviewQuizCard from "./reviewQuizCard";
import EditQuizModal from "./editQuizModal";

const { Title, Text, Paragraph } = Typography;

interface ReviewModalProps {
  visible: boolean;
  creation: IQuizAICreation;
  onClose: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  visible,
  creation,
  onClose,
}) => {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const APIToken = localStorage.getItem("api-token");
  const headers = { Authorization: `Bearer ${APIToken}` };

  const [queue, setQueue] = useState<IQuizAICreationItem[]>([]);
  const [index, setIndex] = useState(0);
  const [fullQuiz, setFullQuiz] = useState<IQuiz | null>(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<
    "approve" | "delete" | null
  >(null);
  const [editVisible, setEditVisible] = useState(false);
  const [stats, setStats] = useState({
    enabled: 0,
    edited: 0,
    deleted: 0,
    skipped: 0,
  });
  const [done, setDone] = useState(false);

  // Initialize queue when modal opens
  useEffect(() => {
    if (visible) {
      const eligible = (creation.quizzes || []).filter(
        (q) => q.status === "for_review"
      );
      setQueue(eligible);
      setIndex(0);
      setSelectedAnswer(null);
      setStats({ enabled: 0, edited: 0, deleted: 0, skipped: 0 });
      setDone(eligible.length === 0);
      setFullQuiz(null);
      setActionLoading(null);
      setEditVisible(false);
    }
  }, [visible, creation.quizzes]);

  const currentItem = queue[index];

  // Fetch full quiz when currentItem changes
  useEffect(() => {
    if (!visible || done || !currentItem) return;
    let cancelled = false;
    setQuizLoading(true);
    setFullQuiz(null);
    setSelectedAnswer(null);
    axios
      .get(`${apiUrl}/quiz/${currentItem.quiz_id}`, { headers })
      .then((res) => {
        if (!cancelled) setFullQuiz(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t("quiz-ai-creation.review.load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setQuizLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem?.quiz_id, visible, done]);

  const advance = useCallback(() => {
    if (index + 1 >= queue.length) {
      setDone(true);
    } else {
      setIndex(index + 1);
    }
  }, [index, queue.length]);

  const handleApprove = useCallback(async () => {
    if (!fullQuiz) return;
    setActionLoading("approve");
    try {
      await axios.put(
        `${apiUrl}/quiz/${fullQuiz.quiz_id}`,
        { ...fullQuiz, status: "enabled" },
        { headers }
      );
      await axios.post(
        `${apiUrl}/quiz/${fullQuiz.quiz_id}/update-children`,
        { model_string: creation.model_string },
        { headers }
      );
      message.success(t("quiz-ai-creation.review.approved_translated"));
      setStats((s) => ({ ...s, enabled: s.enabled + 1 }));
      advance();
    } catch (err) {
      const msg =
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : t("quiz-ai-creation.review.action_failed");
      message.error(msg);
    } finally {
      setActionLoading(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullQuiz, advance, creation.model_string, apiUrl, t]);

  const handleDelete = useCallback(() => {
    if (!fullQuiz) return;
    Modal.confirm({
      title: t("quiz-ai-creation.review.delete_confirm_title"),
      content: t("quiz-ai-creation.review.delete_confirm_body"),
      icon: <ExclamationCircleOutlined />,
      okText: t("buttons.delete"),
      okButtonProps: { danger: true },
      cancelText: t("buttons.cancel"),
      onOk: async () => {
        setActionLoading("delete");
        try {
          await axios.delete(`${apiUrl}/quiz/${fullQuiz.quiz_id}`, { headers });
          message.success(t("quiz-ai-creation.review.deleted"));
          setStats((s) => ({ ...s, deleted: s.deleted + 1 }));
          advance();
        } catch (err) {
          const msg =
            axios.isAxiosError(err) && err.response?.data?.message
              ? err.response.data.message
              : t("quiz-ai-creation.review.action_failed");
          message.error(msg);
        } finally {
          setActionLoading(null);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullQuiz, advance, apiUrl, t]);

  const handleSkip = useCallback(() => {
    if (!currentItem) return;
    setQueue((q) => {
      if (q.length <= 1) return q;
      const next = [...q];
      next.splice(index, 1);
      next.push(currentItem);
      return next;
    });
    // index stays the same (next quiz now slides into this slot), unless we're at the last position
    if (index >= queue.length - 1) {
      setIndex(0);
    }
    setStats((s) => ({ ...s, skipped: s.skipped + 1 }));
    setSelectedAnswer(null);
  }, [currentItem, index, queue.length]);

  const handleEditSavedAndApproved = useCallback(() => {
    setEditVisible(false);
    setStats((s) => ({ ...s, enabled: s.enabled + 1, edited: s.edited + 1 }));
    advance();
  }, [advance]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!visible || editVisible || done || !fullQuiz) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (selectedAnswer === null) {
        if (["1", "2", "3", "4"].includes(e.key)) {
          setSelectedAnswer(Number(e.key));
        }
        return;
      }
      if (actionLoading) return;
      const k = e.key.toLowerCase();
      if (k === "enter" || k === "a") {
        e.preventDefault();
        handleApprove();
      } else if (k === "e") {
        e.preventDefault();
        setEditVisible(true);
      } else if (k === "d") {
        e.preventDefault();
        handleDelete();
      } else if (k === "s" || e.key === "ArrowRight") {
        e.preventDefault();
        handleSkip();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    visible,
    editVisible,
    done,
    fullQuiz,
    selectedAnswer,
    actionLoading,
    handleApprove,
    handleDelete,
    handleSkip,
  ]);

  const total = queue.length;
  const progressPercent =
    total > 0 ? Math.round(((index + (done ? 1 : 0)) / total) * 100) : 0;

  return (
    <>
      <Modal
        title={
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Space>
              <span>{t("quiz-ai-creation.review.title")}</span>
              {!done && total > 0 && (
                <Tag>
                  {t("quiz-ai-creation.review.counter", {
                    current: index + 1,
                    total,
                  })}
                </Tag>
              )}
              {fullQuiz && !done && (
                <Tag color="blue">Level {Number(fullQuiz.level)}</Tag>
              )}
            </Space>
            {!done && total > 0 && (
              <Progress
                percent={progressPercent}
                size="small"
                showInfo={false}
              />
            )}
          </Space>
        }
        visible={visible}
        onCancel={onClose}
        width={780}
        destroyOnClose
        maskClosable={false}
        footer={
          done ? [
            <Button key="close" type="primary" onClick={onClose}>
              {t("buttons.close") || "Close"}
            </Button>,
          ] : null
        }
      >
        {/* Source context */}
        <div
          style={{
            backgroundColor: "#fafafa",
            padding: 12,
            borderRadius: 4,
            marginBottom: 20,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("quiz-ai-creation.fields.url")}:{" "}
          </Text>
          <a href={creation.url} target="_blank" rel="noopener noreferrer">
            {creation.url}
          </a>
          {creation.description && (
            <Paragraph
              style={{ marginTop: 8, marginBottom: 0, fontSize: 13 }}
              type="secondary"
              ellipsis={{ rows: 2, expandable: true, symbol: "more" }}
            >
              {creation.description}
            </Paragraph>
          )}
        </div>

        {done ? (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <CheckCircleOutlined style={{ fontSize: 48, color: "#52c41a" }} />
            <Title level={3} style={{ marginTop: 16 }}>
              {total === 0
                ? t("quiz-ai-creation.review.nothing_to_review")
                : t("quiz-ai-creation.review.done_title")}
            </Title>
            {total > 0 && (
              <Space size="large" style={{ marginTop: 16 }}>
                <Tag color="success">
                  {t("quiz-ai-creation.review.stats_enabled", {
                    n: stats.enabled,
                  })}
                </Tag>
                <Tag color="warning">
                  {t("quiz-ai-creation.review.stats_edited", {
                    n: stats.edited,
                  })}
                </Tag>
                <Tag color="error">
                  {t("quiz-ai-creation.review.stats_deleted", {
                    n: stats.deleted,
                  })}
                </Tag>
                <Tag>
                  {t("quiz-ai-creation.review.stats_skipped", {
                    n: stats.skipped,
                  })}
                </Tag>
              </Space>
            )}
          </div>
        ) : (
          <ReviewQuizCard
            quiz={fullQuiz}
            loading={quizLoading}
            selectedAnswer={selectedAnswer}
            onSelectAnswer={setSelectedAnswer}
            onApprove={handleApprove}
            onEdit={() => setEditVisible(true)}
            onDelete={handleDelete}
            onSkip={handleSkip}
            actionLoading={actionLoading}
          />
        )}
      </Modal>

      <EditQuizModal
        visible={editVisible}
        quiz={fullQuiz}
        creationModelString={creation.model_string}
        onClose={() => setEditVisible(false)}
        onSavedAndApproved={handleEditSavedAndApproved}
      />
    </>
  );
};

export default ReviewModal;
